import { defineRecipe } from '@chakra-ui/react'

export const buttonRecipe = defineRecipe({
  className: 'chakra-button',
  base: {
    alignItems: 'center',
    appearance: 'none',
    borderColor: 'transparent',
    borderRadius: 'md',
    borderWidth: '1px',
    cursor: 'button',
    display: 'inline-flex',
    flexShrink: '0',
    focusVisibleRing: 'outside',
    fontWeight: 'medium',
    isolation: 'isolate',
    justifyContent: 'center',
    lineHeight: '1',
    outline: '0',
    position: 'relative',
    transitionDuration: 'moderate',
    transitionProperty: 'common',
    userSelect: 'none',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    _disabled: {
      layerStyle: 'disabled',
    },
    _icon: {
      flexShrink: '0',
    },
  },
  variants: {
    size: {
      '2xs': {
        h: '6',
        minW: '6',
        textStyle: 'xs',
        px: '2',
        gap: '1',
        _icon: {
          width: '3.5',
          height: '3.5',
        },
      },
      xs: {
        h: '8',
        minW: '8',
        textStyle: 'xs',
        px: '2.5',
        gap: '1',
        _icon: {
          width: '4',
          height: '4',
        },
      },
      sm: {
        h: '9',
        minW: '9',
        px: '3.5',
        textStyle: 'sm',
        gap: '2',
        _icon: {
          width: '4',
          height: '4',
        },
      },
      md: {
        h: '10',
        minW: '10',
        textStyle: 'sm',
        px: '4',
        gap: '2',
        _icon: {
          width: '5',
          height: '5',
        },
      },
      lg: {
        h: '11',
        minW: '11',
        textStyle: 'md',
        px: '5',
        gap: '3',
        _icon: {
          width: '5',
          height: '5',
        },
      },
      xl: {
        h: '12',
        minW: '12',
        textStyle: 'md',
        px: '5',
        gap: '2.5',
        _icon: {
          width: '5',
          height: '5',
        },
      },
      '2xl': {
        h: '16',
        minW: '16',
        textStyle: 'lg',
        px: '7',
        gap: '3',
        _icon: {
          width: '6',
          height: '6',
        },
      },
    },
    variant: {
      cta: {
        bgColor: 'orange.400',
        boxShadow: 'xs',
        color: 'black',
        colorPalette: 'orange',
        fontWeight: 'semibold',
        textShadow: '0 1px 1px {colors.whiteAlpha.100}',
        _hover: {
          bg: 'orange.500',
        },
        _expanded: {
          bg: 'orange.600',
        },
        _active: {
          bg: 'orange.600 !important',
          boxShadow: 'inner !important',
          border: 'blackAlpha.500 !important',
        },
        _after: {
          content: '""',
          position: 'absolute',
          top: '-1px',
          left: '-1px',
          right: '-1px',
          bottom: '-1px',
          borderRadius: 'md',
          bgImage: {
            _light: 'linear-gradient(to bottom, transparent, {colors.whiteAlpha.100})',
            _dark: 'linear-gradient(to bottom, transparent, {colors.blackAlpha.100})',
          },
          boxShadow: 'inset 0 0.025em 0 {colors.whiteAlpha.100}',
        },
      },
      solid: {
        borderColor: 'transparent',
        // boxShadow: 'xs',
        textShadow: {
          _light: '0 0.5px 1px {colors.blackAlpha.400}',
          _dark: '0 0.5px 1px {colors.whiteAlpha.50}',
        },
        _light: {
          bg: 'colorPalette.400',
        },
        _dark: {
          bg: 'colorPalette.600',
        },
        _hover: {
          bg: {
            _light: 'colorPalette.600',
            _dark: 'colorPalette.solid/90',
          },
        },
        _expanded: {
          bg: 'colorPalette.solid/90',
        },
        _after: {
          content: '""',
          position: 'absolute',
          top: '-1px',
          left: '-1px',
          right: '-1px',
          bottom: '-1px',
          borderRadius: 'md',
          bgImage: {
            _light: 'linear-gradient(to bottom, transparent, {colors.whiteAlpha.100})',
            _dark: 'linear-gradient(to bottom, transparent, {colors.blackAlpha.100})',
          },
          boxShadow: {
            _light: 'inset 0 0.025em 0 {colors.blackAlpha.100}',
            _dark: 'inset 0 0.025em 0 {colors.whiteAlpha.100}',
          },
        },
      },
      subtle: {
        bg: 'colorPalette.muted',
        color: 'colorPalette.fg',
        borderColor: 'transparent',
        _hover: {
          bg: 'colorPalette.emphasized',
        },
        _expanded: {
          bg: 'colorPalette.muted',
        },
      },
      surface: {
        bg: 'colorPalette.subtle',
        color: 'colorPalette.fg',
        shadow: '0 0 0px 1px var(--shadow-color)',
        shadowColor: 'colorPalette.muted',
        _hover: {
          bg: 'colorPalette.muted',
        },
        _expanded: {
          bg: 'colorPalette.muted',
        },
      },
      outlineGhost: {
        borderWidth: '1px',
        '--outline-color-legacy': 'colors.colorPalette.muted',
        '--outline-color': 'colors.colorPalette.border',
        borderColor: 'var(--outline-color, var(--outline-color-legacy))',
        color: 'colorPalette.fg',
        _hover: {
          backgroundColor: 'colorPalette.subtle',
        },
        _expanded: {
          bg: 'colorPalette.subtle',
        },
      },
      // Rest matches outlineGhost; hover/active/expanded match solid (fill + sheen).
      outlineSolid: {
        borderWidth: '1px',
        '--outline-color-legacy': 'colors.colorPalette.muted',
        '--outline-color': 'colors.colorPalette.border',
        borderColor: 'var(--outline-color, var(--outline-color-legacy))',
        color: 'colorPalette.fg',
        _after: {
          content: '""',
          position: 'absolute',
          top: '-1px',
          left: '-1px',
          right: '-1px',
          bottom: '-1px',
          borderRadius: 'md',
          opacity: 0,
          bgImage: {
            _light: 'linear-gradient(to bottom, transparent, {colors.whiteAlpha.100})',
            _dark: 'linear-gradient(to bottom, transparent, {colors.blackAlpha.100})',
          },
          boxShadow: {
            _light: 'inset 0 0.025em 0 {colors.blackAlpha.100}',
            _dark: 'inset 0 0.025em 0 {colors.whiteAlpha.100}',
          },
        },
        _hover: {
          borderColor: 'transparent',
          textShadow: {
            _light: '0 0.5px 1px {colors.blackAlpha.400}',
            _dark: '0 0.5px 1px {colors.whiteAlpha.50}',
          },
          bg: {
            _light: 'colorPalette.400',
            _dark: 'colorPalette.600',
          },
          _after: {
            opacity: 1,
          },
        },
        _active: {
          borderColor: 'transparent',
          textShadow: {
            _light: '0 0.5px 1px {colors.blackAlpha.400}',
            _dark: '0 0.5px 1px {colors.whiteAlpha.50}',
          },
          bg: {
            _light: 'colorPalette.600',
            _dark: 'colorPalette.solid/90',
          },
          _after: {
            opacity: 1,
          },
        },
        _expanded: {
          borderColor: 'transparent',
          textShadow: {
            _light: '0 0.5px 1px {colors.blackAlpha.400}',
            _dark: '0 0.5px 1px {colors.whiteAlpha.50}',
          },
          bg: 'colorPalette.solid/90',
          _after: {
            opacity: 1,
          },
        },
      },
      outline: {
        borderWidth: '1px',
        '--outline-color-legacy': 'colors.colorPalette.muted',
        '--outline-color': 'colors.colorPalette.border',
        borderColor: 'var(--outline-color, var(--outline-color-legacy))',
        color: 'colorPalette.fg',
        _hover: {
          bg: 'colorPalette.subtle',
        },
        _expanded: {
          bg: 'colorPalette.subtle',
        },
      },
      ghost: {
        bg: 'transparent',
        color: 'colorPalette.fg',
        _hover: {
          bg: 'colorPalette.subtle',
        },
        _expanded: {
          bg: 'colorPalette.subtle',
        },
      },
      plain: {
        color: 'colorPalette.fg',
      },
    },
  },
  defaultVariants: {
    size: 'lg',
    variant: 'solid',
  },
})
